import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";

import tsLogo from "../../assets/svg/ts-logo.svg";
import logoCollapsed from "../../assets/svg/ts-logo-condensed.svg";
import { makeStyles } from "../../theme/theme";
import type { RootState } from "../../store/state/rootReducer";
import { getInitials } from "../utils/util";
import type { AppDispatch } from "../../store/state/rootStore";
import { getAllOrganizations } from "../../store/actions/organizations";

const useStyles = makeStyles<{ isCustomLogo: boolean }>()((theme, { isCustomLogo }) => ({
  logo: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "50%",
    },
    width: isCustomLogo ? "150px" : "116px",
    height: "auto",
    objectFit: "contain",
  },
  logoCollapsed: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "10%",
    },
    flex: 1,
    width: "35px",
    height: "20px",
  },
  initials: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "12px 6px 12px 7px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    minWidth: "40px",
    maxWidth: "40px",
    minHeight: "40px",
    alignItems: "center",
    justifyContent: "center",
  },
  logoContainer: {
    display: "flex",
    justifyContent: isCustomLogo ? "center" : "flex-start",
    padding: "24px 16px 16px 16px",
    cursor: "pointer",
    maxHeight: "198px",
    minHeight: "65px",
  },
}));

interface OrganizationLogoProps {
  isExpanded: boolean;
}

export const OrganizationLogo = ({ isExpanded }: OrganizationLogoProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const organizationLogo = useSelector((state: RootState) => state.organizations?.organizationLogo);
  const organizations = useSelector((state: RootState) => state.organizations?.organizations);

  const [isTransitioning, setIsTransitioning] = useState(true);

  const orgCode = Object.keys(organizations)[0];
  const organization = organizations[orgCode];
  const logoColl = organization?.logoUrl && getInitials(organization.organization_name);
  const isOrgBrandingFlagOn = useSelector((state: RootState) => state.featureFlags?.flags?.enableOrgBranding);
  const isCustomLogo = isOrgBrandingFlagOn && !!organizationLogo;
  const { classes } = useStyles({ isCustomLogo });

  useEffect(() => {
    if (!organizationLogo) {
      dispatch(getAllOrganizations());
    }
    setIsTransitioning(true);
    const timeoutId = setTimeout(() => {
      setIsTransitioning(false);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [isExpanded, organizationLogo]);

  const logoRender = () => {
    if (isTransitioning) {
      return null;
    }

    if (isOrgBrandingFlagOn && organization?.logoUrl) {
      return isExpanded ? (
        <img
          className={classes.logo}
          src={organizationLogo}
          style={{ borderRadius: "8px" }}
          alt={organization?.organization_name ?? "Organization logo"}
        />
      ) : (
        <Box className={classes.initials}>
          <Typography color="#606971" fontSize="13px" fontWeight={700} lineHeight="16px">
            {logoColl}
          </Typography>
        </Box>
      );
    }

    if (!isOrgBrandingFlagOn || (isOrgBrandingFlagOn && !organization?.logoUrl)) {
      return (
        <img
          className={isExpanded ? classes.logo : classes.logoCollapsed}
          src={isExpanded ? tsLogo : logoCollapsed}
          alt="TheirStory"
        />
      );
    }

    return null;
  };

  return (
    <>
      <Box className={classes.logoContainer} onClick={() => navigate("/")}>
        {logoRender()}
      </Box>
    </>
  );
};
